import 'braid-design-system/reset';

import {
  type ApolloClient,
  ApolloProvider,
  type NormalizedCacheObject,
} from '@apollo/client';
import {
  FeatureFlagProvider,
  FeatureFlagsController,
} from '@seek/candidate-feature-flag';
import { MelwaysProvider } from '@seek/melways-react';
import { VocabProvider } from '@vocab/react';
import { Box, BraidProvider, makeLinkComponent } from 'braid-design-system';
import theme from 'braid-design-system/themes/seekJobs';
import { useState, useEffect } from 'react';
import {
  Outlet,
  Link as ReactRouterLink,
  useLoaderData,
} from 'react-router-dom';

import { useFeatureFlagProviderProps } from 'src/core/utils/featureFlagProviderUtil';
import { getPageName } from 'src/core/utils/pageUtil';
import { useIsRestrictedCountry } from 'src/core/utils/restrictedCountryUtil';
import { getCookie } from 'src/core/utils/storageUtil';
import type { CategoryGroupsLoaderData } from 'src/mapper/groupMapper';
import { ErrorBoundary } from 'src/modules/errorBoundary/ErrorBoundary';
import { AnalyticsProvider } from 'src/provider/analytics';
import { AuthProvider } from 'src/provider/auth';
import ConfigProvider from 'src/provider/config';
import GroupProvider from 'src/provider/group';
import PageProvider from 'src/provider/page';
import { ProfileProvider } from 'src/provider/profile';
import type { ConfigStateContext, RouteProps } from 'src/types';

const CustomLink = makeLinkComponent(({ href, ...restProps }, ref) => {
  const pageName = getPageName(href);
  if (pageName !== 'unknown') {
    return <ReactRouterLink ref={ref} to={href} {...restProps} />;
  }
  return <a ref={ref} href={href} {...restProps} />;
});

const FeatureGatedComponent = ({
  client,
  config,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  config: ConfigStateContext;
}) => {
  const [isAllowed, setIsAllowed] = useState(true);
  const isRestrictedCountry = useIsRestrictedCountry(config.countryCode);
  useEffect(() => {
    const needsRestriction =
      config.environment === 'production' && isRestrictedCountry;
    if (needsRestriction) {
      const hasAccess =
        getCookie('access_prod') === 'true' ||
        localStorage.getItem('access_prod') === 'true';

      setIsAllowed(hasAccess);
    } else {
      setIsAllowed(true);
    }
  }, [config.environment, isRestrictedCountry]);

  return config.environment === 'production' && !isAllowed ? (
    <ErrorBoundary />
  ) : (
    <Outlet context={{ client, config }} />
  );
};

const ContextComponent = ({
  client,
  config,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  config: ConfigStateContext;
}) => (
  <FeatureFlagProvider {...useFeatureFlagProviderProps(config)}>
    <VocabProvider language={config.language} locale={config.locale}>
      <AnalyticsProvider>
        <Box role="main">
          <FeatureFlagsController />
          <FeatureGatedComponent client={client} config={config} />
        </Box>
      </AnalyticsProvider>
    </VocabProvider>
  </FeatureFlagProvider>
);

export const App = ({ client, config, clientOrServer }: RouteProps) => {
  const mode: 'client' | 'server' = clientOrServer ? clientOrServer : 'server';
  const data = useLoaderData() as {
    categoryGroups: CategoryGroupsLoaderData[];
  };

  return (
    <ConfigProvider config={{ ...config, clientOrServer: mode }}>
      <PageProvider>
        <ApolloProvider client={client}>
          <BraidProvider theme={theme} linkComponent={CustomLink}>
            <AuthProvider>
              <ProfileProvider>
                <GroupProvider value={data.categoryGroups}>
                  {mode === 'server' && (
                    <MelwaysProvider.Server
                      site={config.site}
                      staging={config.environment === 'staging'}
                      url={config.absoluteUrl}
                    >
                      <ContextComponent client={client} config={config} />
                    </MelwaysProvider.Server>
                  )}
                  {mode === 'client' && (
                    <MelwaysProvider.Client
                      site={config.site}
                      staging={config.environment === 'staging'}
                    >
                      <ContextComponent client={client} config={config} />
                    </MelwaysProvider.Client>
                  )}
                </GroupProvider>
              </ProfileProvider>
            </AuthProvider>
          </BraidProvider>
        </ApolloProvider>
      </PageProvider>
    </ConfigProvider>
  );
};
